import { useState, useEffect, useMemo, useRef } from 'react';
import './styles.css';
import { getLayout5ThemeStyle } from './theme';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, Phone, MessageCircle, MapPin, Mail, Video, CreditCard, Shield, Car, FileText, Wrench, Heart, Search, Users, Package, Settings, Gauge, Handshake, HandCoins, X, Navigation, ChevronDown, Layout, Instagram, Facebook, Youtube, Linkedin, Twitter } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Link, useLocation } from 'react-router-dom';
import { ConfigModal } from '@/components/ConfigModal';
import { ServiceModal } from '@/components/ServiceModal';
import { StoreSelectionModal } from '@/components/StoreSelectionModal';
import { LayoutSelectionModal } from '@/components/LayoutSelectionModal';
import { AccountIdModal } from '@/components/AccountIdModal';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import { getAccountId } from '@/lib/obfuscate';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { getStoreBrandAssets } from '@/lib/storeBrand';
import { HeaderBrandLogo } from '@/components/HeaderBrandLogo';
import { useIsMobile } from '@/hooks/use-mobile';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

interface HeaderProps {
  config?: any;
}

export const Header = ({ config: propConfig }: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
  const [isAccountIdModalOpen, setIsAccountIdModalOpen] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { config, vehicles, accountId } = useAppData();
  const { colors } = useTheme();
  const { selectedStoreIndex, setSelectedStoreIndex, availableStores, selectedStore } = useStore();

  // Get original account ID from environment (for button visibility check)
  const originalAccountId = getAccountId();
  const shouldShowDevButtons = originalAccountId === '677e7e1b763f79265b9228db';

  // Use context config if no prop config is provided
  const currentConfig = propConfig || config;

  // Use selected store from context
  const store = selectedStore || currentConfig.stores[0];

  // Check if should show store selector (more than 1 available store)
  const showStoreSelector = availableStores.length > 1;

  // Update favorites count - only count vehicles that still exist
  useEffect(() => {
    const updateFavoritesCount = async () => {
      try {
        const favoriteIds = await secureGetItem('favorites');

        if (favoriteIds && vehicles.length > 0 && Array.isArray(favoriteIds)) {
          // Filter to only count favorites that still exist in the vehicle list
          const existingFavorites = favoriteIds.filter(id =>
            vehicles.some(v => v.id === id)
          );

          setFavoritesCount(existingFavorites.length);

          // Clean up localStorage if there are sold vehicles
          if (existingFavorites.length !== favoriteIds.length) {
            await secureSetItem('favorites', existingFavorites);
          }
        } else {
          setFavoritesCount(0);
        }
      } catch (error) {
        setFavoritesCount(0);
      }
    };

    // Initial count
    updateFavoritesCount();

    // Listen for favorites updates
    window.addEventListener('favoritesUpdated', updateFavoritesCount);
    window.addEventListener('storage', updateFavoritesCount);

    return () => {
      window.removeEventListener('favoritesUpdated', updateFavoritesCount);
      window.removeEventListener('storage', updateFavoritesCount);
    };
  }, [vehicles]);

  const headerColor = colors.header;
  const menuColor = colors.menu;
  const primaryColor = colors.primary;
  const buttonColor = colors.button;

  const { pathname } = useLocation();
  const social = currentConfig?.socialNetworks;
  const storeEmail = currentConfig?.email;
  const storeAddress = store?.place?.address;

  const isNavActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  // Get main service info
  const mainServiceId = store.mainService;
  const mainService = store.services?.find((s: any) => s.service === mainServiceId);
  const isMobile = useIsMobile();
  const hasWebBanners = store.banners?.filter((v: any) => v.platform === (isMobile ? 'mobile' : 'web')).length > 0;

  const serviceInfo: Record<string, { icon: any; title: string; description: string; image: string; color: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', image: videoCall, color: 'bg-blue-500' },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', image: valuation, color: 'bg-green-500' },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', image: consignment, color: 'bg-purple-500' },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', image: consortium, color: 'bg-teal-500' },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', image: forwardingAgent, color: 'bg-orange-500' },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', image: financing, color: 'bg-blue-600' },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', image: partsAccessories, color: 'bg-gray-600' },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', image: scheduleReview, color: 'bg-slate-600' },
    '65dcb7238eca108d87c91dba': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', image: insurance, color: 'bg-red-500' },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', image: scheduleServices, color: 'bg-indigo-600' },
    '65dcb7fe8eca108d87c91dbe': { icon: FileText, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', image: fipeTable, color: 'bg-cyan-500' },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', image: testdrive, color: 'bg-indigo-500' },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', image: directSales, color: 'bg-green-600' }
  };

  const mainServiceInfo = mainServiceId ? serviceInfo[mainServiceId] : null;
  const MainServiceIcon = mainServiceInfo?.icon || MessageCircle;
  const mainServiceTitle = mainServiceInfo?.title || 'Fale Conosco';

  // Get service image (from config or default)
  const mainServiceImage = mainService?.image || mainServiceInfo?.image;

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar',
      '65dceeac8eca108d87c91dc6': 'Agendar',
      '65dcef098eca108d87c91dc7': 'Agendar',
      '65dcb5e78eca108d87c91db8': 'Agendar',
      '65dcb7898eca108d87c91dbc': 'Simular',
      '65dcb7548eca108d87c91dbb': 'Avaliar',
      '65dcb7c58eca108d87c91dbd': 'Avaliar',
      '65dcb7238eca108d87c91dba': 'Cotar',
      '65dcb6528eca108d87c91db9': 'Cotar',
      '6628fd535bda2ae51f6fc5ff': 'Cotar',
      '65dcef468eca108d87c91dc8': 'Solicitar',
      '65dcb8548eca108d87c91dbf': 'Solicitar',
      '65dcb7fe8eca108d87c91dbe': 'Consultar',
    };
    return buttonTexts[serviceId] || 'Solicitar';
  };

  const menuItems = [
    { label: 'Início', href: '/', isLink: true },
    { label: 'Estoque', href: '/estoque', isLink: true },
    { label: 'Serviços', href: '/servicos', isLink: true },
    { label: 'Contato', href: '/contato', isLink: true }
  ];

  const { hasLogo, headerBrandSrc: brandImageSrc } = getStoreBrandAssets(currentConfig, store);

  const renderBrandMark = (variant: 'nav' | 'sheet' = 'nav') => {
    if (!brandImageSrc) return null;

    return (
      <div className={variant === 'sheet' ? 'wl5-logo-safe wl5-logo-safe--sheet' : 'wl5-logo-safe'}>
        <HeaderBrandLogo
          src={brandImageSrc}
          alt={currentConfig.name}
          variant={variant}
        />
      </div>
    );
  };

  const handleStoreChange = (index: number) => {
    setSelectedStoreIndex(index);
  };

  const openMainServiceModal = () => {
    setIsHoverCardOpen(false);
    setIsOpen(false);
    setIsServiceModalOpen(true);
  };

  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const syncHeaderHeight = () => {
      const height = `${el.offsetHeight}px`;
      document.documentElement.style.setProperty('--wl5-header-h', height);
      el.parentElement?.style.setProperty('--wl5-header-h', height);
    };

    syncHeaderHeight();
    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(el);
    window.addEventListener('resize', syncHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncHeaderHeight);
    };
  }, [showStoreSelector, store.company, storeEmail, storeAddress]);

  const storeDistances = useMemo(() => {
    if (!userLocation) return {} as Record<string, number>;
    const distances: Record<string, number> = {};
    availableStores.forEach((storeOption: any) => {
      if (storeOption.place?.latitude && storeOption.place?.longitude) {
        distances[storeOption._id] = getDistanceToStore(
          userLocation.lat,
          userLocation.lng,
          storeOption.place.latitude,
          storeOption.place.longitude
        );
      }
    });
    return distances;
  }, [userLocation, availableStores]);

  return (
    <header
      ref={headerRef}
      className="layout-5 fixed top-0 left-0 right-0 z-50"
      style={getLayout5ThemeStyle(colors)}
    >
      {/* Topbar — contato + redes sociais */}
      <div className="wl5-topbar hidden lg:block" style={{ backgroundColor: headerColor, color: menuColor }}>
        <div className="wl5-container flex items-center justify-between h-9">
          <div className="flex items-center gap-6 min-w-0">
            {store.phone?.formatted && (
              <a href={store.phone.ios} className="wl5-topbar-link flex items-center gap-2 shrink-0">
                <Phone className="h-3.5 w-3.5 shrink-0 wl5-topbar-icon" />
                <span>{store.phone.formatted}</span>
              </a>
            )}
            {storeEmail && (
              <a href={`mailto:${storeEmail}`} className="wl5-topbar-link flex items-center gap-2 shrink-0">
                <Mail className="h-3.5 w-3.5 shrink-0 wl5-topbar-icon" />
                <span className="truncate max-w-[220px]">{storeEmail}</span>
              </a>
            )}
            {showStoreSelector ? (
              <button
                type="button"
                onClick={() => setIsStoreModalOpen(true)}
                className="wl5-topbar-link flex items-center gap-2 min-w-0"
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 wl5-topbar-icon" />
                <span className="truncate max-w-[240px]">{store.company || `Loja ${selectedStoreIndex + 1}`}</span>
                <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
              </button>
            ) : storeAddress ? (
              <span className="wl5-topbar-link flex items-center gap-2 min-w-0">
                <MapPin className="h-3.5 w-3.5 shrink-0 wl5-topbar-icon" />
                <span className="truncate max-w-[280px]">{storeAddress}</span>
              </span>
            ) : null}
          </div>

          {social && (
            <div className="flex items-center gap-2 shrink-0">
              {social.twitter && (
                <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="wl5-topbar-social" aria-label="X">
                  <Twitter className="h-3.5 w-3.5" />
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="wl5-topbar-social" aria-label="Instagram">
                  <Instagram className="h-3.5 w-3.5" />
                </a>
              )}
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="wl5-topbar-social" aria-label="Facebook">
                  <Facebook className="h-3.5 w-3.5" />
                </a>
              )}
              {social.youtube && (
                <a href={social.youtube} target="_blank" rel="noopener noreferrer" className="wl5-topbar-social" aria-label="YouTube">
                  <Youtube className="h-3.5 w-3.5" />
                </a>
              )}
              {social.linkedin && (
                <a href={social.linkedin} target="_blank" rel="noopener noreferrer" className="wl5-topbar-social" aria-label="LinkedIn">
                  <Linkedin className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navbar principal */}
      <div className="wl5-header-bar" style={{ backgroundColor: headerColor }}>
      <div className="wl5-container">
        <div className="flex items-center justify-between h-14 lg:h-16 gap-4">
          <Link to="/" className="flex items-center flex-shrink-0 gap-2.5 min-w-0">
            {renderBrandMark()}
            {!hasLogo && (
              <div className="hidden sm:block min-w-0">
                <span className="font-bold text-sm block leading-tight truncate" style={{ color: menuColor }}>{currentConfig.name}</span>
                {currentConfig.slogan && <span className="text-xs truncate block opacity-75" style={{ color: menuColor }}>{currentConfig.slogan}</span>}
              </div>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center" aria-label="Menu principal">
            {menuItems.map((item) => (
              item.isLink ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`wl5-nav-link ${isNavActive(item.href) ? 'is-active' : ''}`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  className={`wl5-nav-link ${isNavActive(item.href) ? 'is-active' : ''}`}
                >
                  {item.label}
                </a>
              )
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link
              to="/favoritos"
              className="wl5-nav-pill wl5-nav-pill--secondary relative"
            >
              <Heart className="h-4 w-4" />
              Favoritos
              {favoritesCount > 0 && (
                <span className="wl5-nav-badge">
                  {favoritesCount > 99 ? '99+' : favoritesCount}
                </span>
              )}
            </Link>

            {hasWebBanners && mainService ? (
              <>
                <HoverCard open={isHoverCardOpen} onOpenChange={setIsHoverCardOpen} openDelay={200}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      className="wl5-nav-pill wl5-nav-pill--secondary"
                    >
                      {mainServiceTitle}
                      <MainServiceIcon className="h-4 w-4" />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-[320px] p-0 overflow-hidden border-0 shadow-2xl rounded-2xl" side="bottom" align="end">
                    {mainServiceInfo && (
                      <div>
                        <div className="relative h-32 overflow-hidden">
                          <img src={mainServiceImage} alt={mainServiceInfo.title} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          <div className="absolute bottom-3 left-3 right-3">
                            <h3 className="text-white font-bold text-sm">{mainServiceInfo.title}</h3>
                          </div>
                        </div>
                        <div className="p-4 space-y-3">
                          <p className="text-xs text-muted-foreground">{mainServiceInfo.description}</p>
                          <Button
                            type="button"
                            onClick={openMainServiceModal}
                            className="w-full font-semibold rounded-xl wl5-nav-pill--cta"
                          >
                            {getServiceButtonText(mainServiceId)}
                          </Button>
                        </div>
                      </div>
                    )}
                  </HoverCardContent>
                </HoverCard>
                <ServiceModal service={mainService} config={currentConfig} isOpen={isServiceModalOpen} onOpenChange={setIsServiceModalOpen} />
              </>
            ) : (
              <button
                type="button"
                className="wl5-nav-pill wl5-nav-pill--secondary"
                onClick={(e) => { e.preventDefault(); openWhatsApp(store.whatsapp?.ios); }}
              >
                Contato
                <MessageCircle className="h-4 w-4" />
              </button>
            )}

            {shouldShowDevButtons && (
              <>
                <Button variant="ghost" size="icon" className="wl5-header-icon-btn rounded-lg" onClick={() => setIsLayoutModalOpen(true)} aria-label="Selecionar layout" style={{ color: menuColor }}>
                  <Layout className="h-5 w-5" />
                </Button>
                <Button variant="ghost" size="icon" className="wl5-header-icon-btn rounded-lg" onClick={() => setIsAccountIdModalOpen(true)} aria-label="Alterar ID da conta" style={{ color: menuColor }}>
                  <Settings className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>

          <div className="flex lg:hidden items-center gap-0.5">
            <a href={store.phone?.ios} className="p-2 rounded-lg hover:opacity-80 transition-opacity" style={{ color: menuColor }} aria-label="Ligar">
              <Phone className="h-5 w-5" />
            </a>
            <button onClick={(e) => { e.preventDefault(); openWhatsApp(store.whatsapp?.ios); }} className="p-2 rounded-lg hover:opacity-80 transition-opacity" style={{ color: menuColor }} aria-label="WhatsApp">
              <MessageCircle className="h-5 w-5" />
            </button>
            {showStoreSelector && (
              <button onClick={() => setIsStoreModalOpen(true)} className="p-2 rounded-lg hover:opacity-80 transition-opacity" style={{ color: menuColor }} aria-label="Selecionar loja">
                <MapPin className="h-5 w-5" />
              </button>
            )}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="wl5-header-icon-btn rounded-lg" aria-label="Abrir menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md p-0 border-l border-[var(--wl5-border)] [&>button]:hidden layout-5">
                <div className="flex flex-col h-full bg-white">
                  <SheetHeader className="px-5 py-6 border-b border-[var(--wl5-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {renderBrandMark('sheet')}
                        <div className="text-left">
                          <SheetTitle className="text-base font-bold">{currentConfig.name}</SheetTitle>
                          {currentConfig.slogan && <p className="text-xs text-[var(--wl5-muted)] mt-0.5">{currentConfig.slogan}</p>}
                        </div>
                      </div>
                      <button onClick={() => setIsOpen(false)} className="p-2 rounded-lg hover:bg-[var(--wl5-bg)]" aria-label="Fechar menu">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto py-4 px-3">
                    <nav className="flex flex-col gap-0.5">
                      {menuItems.map((item: any) => (
                        item.isLink ? (
                          <Link key={item.label} to={item.href} onClick={() => setIsOpen(false)} className="flex items-center px-4 py-3.5 rounded-xl text-base font-semibold hover:bg-[var(--wl5-bg)] transition-colors">
                            {item.label}
                          </Link>
                        ) : (
                          <a key={item.label} href={item.href} onClick={() => setIsOpen(false)} className="flex items-center px-4 py-3.5 rounded-xl text-base font-semibold hover:bg-[var(--wl5-bg)] transition-colors">
                            {item.label}
                          </a>
                        )
                      ))}
                    </nav>

                    <Separator className="my-4" />

                    <Link to="/favoritos" onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[var(--wl5-bg)]">
                      <Heart className="h-5 w-5" />
                      <span className="font-medium">Favoritos</span>
                      {favoritesCount > 0 && (
                        <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: buttonColor }}>{favoritesCount}</span>
                      )}
                    </Link>

                    {showStoreSelector && (
                      <>
                        <Separator className="my-4" />
                        <p className="px-4 text-xs font-bold tracking-widest text-[var(--wl5-muted)] mb-2">LOJAS</p>
                        <div className="space-y-0.5">
                          {availableStores.map((storeOption: any, index: number) => {
                            const distance = storeDistances[storeOption._id] || null;
                            return (
                              <button
                                key={storeOption._id}
                                onClick={() => { handleStoreChange(index); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${selectedStoreIndex === index ? 'bg-[var(--wl5-bg)] font-semibold' : 'hover:bg-[var(--wl5-bg)]'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 flex-shrink-0 text-[var(--wl5-muted)]" />
                                    <div>
                                      <span className="text-sm">{storeOption.company}</span>
                                      {distance !== null && (
                                        <span className="text-xs text-[var(--wl5-muted)] flex items-center gap-1 mt-0.5">
                                          <Navigation className="h-3 w-3" />
                                          {formatDistance(distance)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {selectedStoreIndex === index && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: buttonColor }} />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  {hasWebBanners && mainService && (
                    <div className="p-4 border-t border-[var(--wl5-border)]">
                      <ServiceModal service={mainService} config={currentConfig}>
                        <Button
                          className="w-full font-semibold rounded-full h-11 wl5-nav-pill--cta"
                        >
                          <MainServiceIcon className="h-5 w-5 mr-2" />
                          {mainServiceTitle}
                        </Button>
                      </ServiceModal>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      </div>

      {/* Store Selection Modal */}
      {showStoreSelector && (
        <StoreSelectionModal
          config={currentConfig}
          isOpen={isStoreModalOpen}
          onClose={() => setIsStoreModalOpen(false)}
        />
      )}

      {/* Layout Selection Modal */}
      {shouldShowDevButtons && (
        <LayoutSelectionModal
          isOpen={isLayoutModalOpen}
          onOpenChange={setIsLayoutModalOpen}
        />
      )}

      {/* Account ID Modal */}
      {shouldShowDevButtons && (
        <AccountIdModal
          isOpen={isAccountIdModalOpen}
          onOpenChange={setIsAccountIdModalOpen}
        />
      )}

      {/* Services Modal */}
      <Dialog open={isServicesModalOpen} onOpenChange={setIsServicesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col gap-0">
          {/* Fixed Header */}
          <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
            <h2 className="text-2xl font-bold text-foreground">Nossos Serviços</h2>
            <p className="text-muted-foreground mt-1">Conheça todos os serviços que oferecemos</p>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {store.services?.map((service: any) => {
                const info = serviceInfo[service.service];
                if (!info) return null;

                const ServiceIcon = info.icon;

                return (
                  <ServiceModal key={service.service} service={service} config={currentConfig}>
                    <button className="w-full text-left group">
                      <div className="border border-border/50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-primary/50">
                        {/* Service Image */}
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-muted to-muted/50">
                          <img
                            src={service.image || info.image}
                            alt={info.title}
                            className="w-full h-full object-cover transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute top-2 right-2">
                            <div className={`w-10 h-10 ${info.color} rounded-lg flex items-center justify-center backdrop-blur-sm bg-white/10`}>
                              <ServiceIcon className="h-5 w-5 text-white" />
                            </div>
                          </div>
                        </div>

                        {/* Service Info */}
                        <div className="p-4">
                          <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                            {info.title}
                          </h3>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {info.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  </ServiceModal>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
